// DocumentViewer.tsx
import { JSX, useEffect, useState } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Document, DocumentStatus, FetchDocumentsByTenderIdQuery, FetchDocumentsByTenderIdQueryVariables, FetchDocumentsByTenderIdDocument } from "@/graphql/__generated__/graphql";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { DocumentPage } from "./Components";
import { pdfjs } from "react-pdf";
import { getFileNameFromDocUrl } from "./utils";
import { Button } from "@/components/ui/button";
import { ChevronsRightIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useQuery } from "@apollo/client/react";

// Configure the worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface Props {
  isOpen?: boolean;
  onClose?: () => void;
}

/**
 * Component for viewing and interacting with documents within a tender.
 *
 * @returns {JSX.Element} The rendered DocumentViewer component.
 *
 * @component
 *
 * @example
 * // Usage example:
 * <DocumentViewer hideTopBar={true} />
 *
 * @remarks
 * This component uses the `useTranslation` hook for internationalization and the `useOutletContext` hook to access the tender data.
 * It groups documents by their type and allows users to switch between different document types using tabs.
 * The component also handles URL search parameters to select a specific document and page within the document.
 *
 * @hook
 * - `useTranslation` - For internationalization.
 * - `useOutletContext` - To access the tender data.
 * - `useState` - To manage the selected tab and selected documents state.
 * - `useSearchParams` - To read URL search parameters.
 * - `useEffect` - To initialize the selected documents based on the available documents and URL parameters.
 */
const DocumentViewer = (props: Props): JSX.Element => {
  const { isOpen, onClose } = props;

  // Hooks
  const { t } = useTranslation(["translation", "common"]);
  const { tenderByIdData } = useOutletContext<EditTenderContext>();
  const tenderData = tenderByIdData?.fetchTenderById;

  const { data } = useQuery<FetchDocumentsByTenderIdQuery, FetchDocumentsByTenderIdQueryVariables>(FetchDocumentsByTenderIdDocument, {
    variables: { tenderId: tenderData?.id, status: DocumentStatus.InKnowledgeBase },
    fetchPolicy: "cache-and-network",
  });

  const documents = data?.fetchDocumentsByTenderId;

  const documentsGroupedByType =
    documents?.reduce((acc: { [key: string]: Document[] }, document) => {
      if (acc[document.type]) {
        acc[document.type].push(document);
      } else {
        acc[document.type] = [document];
      }
      return acc;
    }, {}) || {};

  const documentTypes = Object.keys(documentsGroupedByType);

  // States
  const [selectedTab, setSelectedTab] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<{
    [key: string]: Document;
  }>({});

  // URL search parameters
  const [searchParams] = useSearchParams();
  const fileParam = searchParams.get("file");
  const pageParam = searchParams.get("page");

  /**
   * Initialize the selected documents based on the available documents and URL parameters.
   */
  useEffect(() => {
    if (documents && documents.length > 0) {
      const initialSelectedDocuments: { [key: string]: Document } = {};
      let initialTab = documentTypes[0];

      documentTypes.forEach((type) => {
        initialSelectedDocuments[type] = documentsGroupedByType[type][0];
      });

      if (fileParam) {
        const fileNameFromParam = getFileNameFromDocUrl(
          decodeURIComponent(fileParam)
        );
        let matchingDocument: Document | undefined;
        let matchingType: string | undefined;

        for (const type of documentTypes) {
          const docsOfType = documentsGroupedByType[type];
          const doc = docsOfType.find(
            (doc) => getFileNameFromDocUrl(doc.url) === fileNameFromParam
          );
          if (doc) {
            matchingDocument = doc;
            matchingType = type;
            break;
          }
        }

        if (matchingDocument && matchingType) {
          initialSelectedDocuments[matchingType] = matchingDocument;
          initialTab = matchingType;
        }
      }

      setSelectedDocuments(initialSelectedDocuments);
      setSelectedTab(initialTab);
    }
  }, [documents, fileParam]);

  return (
    <div className="pl-2 pt-2 pr-4 bg-white">
      <Tabs className="w-full" value={selectedTab || documentTypes[0]} onValueChange={setSelectedTab}>
        <TabsList className="flex w-full justify-center h-[36px]">
          {isOpen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="default" size="icon" className="flex items-center gap-2 h-full mr-2" onClick={onClose}>
                  <ChevronsRightIcon className="size-4"/>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                {t("components:documentchat.chat.hidedocuments")}
              </TooltipContent>
            </Tooltip>
          )}
          {documentTypes.map((type) => (
            <TabsTrigger key={type} value={type} className="flex items-center gap-2">
              {t(
                `edittender.details.formgroups.document.labels.${type
                  .toLowerCase()
                  .replace(/_/g, "")}`
              )}
              <p className="bg-slate-800 text-white rounded-full icon-sm flex items-center justify-center z-[2]">
                {documentsGroupedByType[type].length}
              </p>
            </TabsTrigger>
          ))}
        </TabsList>
        {documentTypes.map((type) => {
          const docs = documentsGroupedByType[type];
          const selectedDocument = selectedDocuments[type];

          const onSelectDocument = (newDocument: Document) => {
            setSelectedDocuments((prev) => ({
              ...prev,
              [type]: newDocument,
            }));
          };

          return (
            <TabsContent key={type} value={type}>
              <DocumentPage
                pdfDocument={selectedDocument}
                documents={docs}
                onSelectDocument={onSelectDocument}
                initialPage={pageParam ? parseInt(pageParam) : undefined}
              />
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
};

export default DocumentViewer;
